import { createAsyncThunk } from '@reduxjs/toolkit';
import $api from 'http/index';
import { getTimeZone } from 'helpers/getTimeZone';

const BASE = process.env.REACT_APP_BASE_URL;

export const WORKLOG_LIMIT = 40;

// Captured once at module load — mirrors the legacy default range (± 1 year from boot).
const defaultDate = [
  new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
  new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
];

const authOptions = () => ({
  headers: {
    'X-Auth-Token': localStorage.getItem('authorization-token'),
    'X-User-TZ': getTimeZone(),
  },
});

// Param builder for the active list request.
const buildActiveParams = ({
  searchValue,
  filter = { dates: defaultDate },
  page,
  userId,
  limit = WORKLOG_LIMIT,
  isClear,
  skip = !page || isClear ? '0' : page * WORKLOG_LIMIT,
}) => ({
  searchValue,
  ...(filter && filter),
  userId,
  limit,
  skip,
  from: filter.dates[0],
  to: filter.dates[1],
  dates: null,
});

export const getActiveWorklogs = createAsyncThunk(
  'worklogs/getActive',
  async (payload, { rejectWithValue }) => {
    const { page, isClear, limit = WORKLOG_LIMIT, isTodayMode, shouldBeToday } = payload;
    const params = buildActiveParams(payload);

    try {
      const res = await $api.get(`${BASE}/sheets`, { ...authOptions(), params });
      return {
        data: res.data,
        navIds: res.data?.slice()?.reverse().map((e) => e._id),
        hasMore: isTodayMode || res.data.length === limit,
        clear: isClear || !page,
        shouldBeToday,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

// Returns worklogs where each carries a single submission entry.
export const getSubmittedWorklogs = createAsyncThunk(
  'worklogs/getSubmitted',
  async (payload, { rejectWithValue }) => {
    const {
      searchValue,
      filter = { dates: defaultDate },
      page,
      userId,
      limit = WORKLOG_LIMIT,
      skip = !page ? '0' : page * WORKLOG_LIMIT,
      isClear,
    } = payload;

    const params = {
      searchValue,
      ...(filter && filter),
      userId,
      limit,
      skip,
      dates: null,
      withSubmissions: 'true',
      submitted: 'true',
    };

    const from = filter.dates[0];
    const to = filter.dates[1];
    if (from && to) {
      params.from = from;
      params.to = to;
    }

    try {
      const res = await $api.get(`${BASE}/sheets`, { ...authOptions(), params });
      return {
        data: res.data,
        navIds: res.data.map((e) => e._id),
        hasMore: res.data.length === limit,
        clear: isClear || !page,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const getForemanDataWorklogs = createAsyncThunk(
  'worklogs/getForeman',
  async (payload, { rejectWithValue }) => {
    const {
      searchValue,
      filter = { dates: defaultDate },
      page,
      userId,
      limit = WORKLOG_LIMIT,
      skip = !page ? '0' : page * WORKLOG_LIMIT,
      isClear,
    } = payload;

    const params = {
      searchValue,
      ...(filter && filter),
      userId,
      limit,
      skip,
      from: filter.dates[0],
      to: filter.dates[1],
      isCrewLeader: 'true',
      dates: null,
    };

    try {
      const res = await $api.get(`${BASE}/sheets`, { ...authOptions(), params });
      return {
        data: res.data,
        navIds: res.data.map((e) => e._id),
        hasMore: res.data.length === limit,
        clear: isClear || !page,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const postNewWorklogSheet = async (data, worklogHeaders) => {
  const options = authOptions();
  if (worklogHeaders?.tenantLocationId) {
    options.headers['x-user-location-id'] = worklogHeaders.tenantLocationId;
  }
  await $api.post(`${BASE}/sheets`, data, options);
};

export const fetchDeleteActiveSheet = createAsyncThunk(
  'worklogs/deleteActive',
  async (id, { rejectWithValue }) => {
    try {
      await $api.delete(`${BASE}/sheets/${id}`, authOptions());
      return id;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const fetchDeleteCrewSheet = createAsyncThunk(
  'worklogs/deleteCrew',
  async (id, { rejectWithValue }) => {
    try {
      await $api.delete(`${BASE}/sheets/${id}`, authOptions());
      return id;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const fetchDeleteSubmittedSheet = createAsyncThunk(
  'worklogs/deleteSubmitted',
  async (id, { rejectWithValue }) => {
    try {
      await $api.delete(`${BASE}/submissions/${id}`, authOptions());
      return id;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const setChangeArchivedActiveState = createAsyncThunk(
  'worklogs/archiveActive',
  async ({ _id, archived, openSnackbar }, { rejectWithValue }) => {
    try {
      await $api.patch(`${BASE}/sheets/${_id}/archive`, { archived }, authOptions());
      openSnackbar('success', archived ? 'Sheet Archived' : 'Sheet Reactivated');
      return _id;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const setChangeArchivedCrewState = createAsyncThunk(
  'worklogs/archiveCrew',
  async ({ _id, archived, openSnackbar }, { rejectWithValue }) => {
    try {
      await $api.patch(`${BASE}/sheets/${_id}/archive`, { archived }, authOptions());
      openSnackbar('success', archived ? 'Sheet Archived' : 'Sheet Reactivated');
      return _id;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);
